import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { hamaColors } from '../data/hamaColors';
import { rgbToLab, deltaE } from '../utils/colorConverter';
import { adjustImage } from '../utils/imageFilters';

const { FiDownload, FiZoomIn, FiZoomOut, FiGrid, FiList, FiType, FiLayers, FiEye, FiLoader } = FiIcons;

const PixelArtCanvas = ({ originalImage, settings, isProcessing, onProcessingChange }) => {
  const canvasRef = useRef(null);
  const displayCanvasRef = useRef(null);
  const [zoom, setZoom] = useState(0.8);
  const [viewMode, setViewMode] = useState('beads');
  const [showSymbols, setShowSymbols] = useState(false);
  const [showPlaques, setShowPlaques] = useState(true);
  const [beadCount, setBeadCount] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  const hamaLabColors = React.useMemo(() => 
    hamaColors.map(c => ({ ...c, lab: rgbToLab(c.r, c.g, c.b) })), []
  );

  const findClosestColor = (r, g, b) => {
    const targetLab = rgbToLab(r, g, b);
    let minDistance = Infinity;
    let closest = hamaColors[0];
    for (const color of hamaLabColors) {
      const dist = deltaE(targetLab, color.lab);
      if (dist < minDistance) {
        minDistance = dist;
        closest = color;
      }
    }
    return closest;
  };

  const processImage = () => {
    if (!originalImage) return;
    onProcessingChange(true);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const { width, height } = settings;
      canvas.width = width;
      canvas.height = height;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      let imageData = ctx.getImageData(0, 0, width, height);
      let data = new Float32Array(imageData.data);

      adjustImage(data, settings);

      const counts = {};
      const finalBeads = [];

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const closest = findClosestColor(data[i], data[i+1], data[i+2]);
          finalBeads.push({ x, y, color: closest });
          counts[closest.id] = (counts[closest.id] || 0) + 1;

          if (settings.useDithering) {
            const errR = data[i] - closest.r, errG = data[i+1] - closest.g, errB = data[i+2] - closest.b;
            const distribute = (nx, ny, w) => {
              if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                const ni = (ny * width + nx) * 4;
                data[ni] += errR * w; data[ni+1] += errG * w; data[ni+2] += errB * w;
              }
            };
            distribute(x+1, y, 7/16); distribute(x-1, y+1, 3/16); distribute(x, y+1, 5/16); distribute(x+1, y+1, 1/16);
          }
        }
      }

      renderStudio(finalBeads, width, height);
      setBeadCount(counts);
      onProcessingChange(false);
    };
    img.src = originalImage;
  };

  const renderStudio = (beads, w, h) => {
    const canvas = displayCanvasRef.current;
    if (!canvas) return;
    const scale = 32;
    canvas.width = w * scale + 40;
    canvas.height = h * scale + 40;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = 'bold 12px sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'center';
    for(let x=0; x<w; x++) {
      if ((x+1) % 5 === 0 || x === 0) ctx.fillText(x+1, x * scale + scale/2 + 40, 25);
    }
    ctx.textAlign = 'right';
    for(let y=0; y<h; y++) {
      if ((y+1) % 5 === 0 || y === 0) ctx.fillText(y+1, 30, y * scale + scale/2 + 40);
    }

    ctx.translate(40, 40);

    beads.forEach(bead => {
      const cx = bead.x * scale + scale/2;
      const cy = bead.y * scale + scale/2;

      if (viewMode === 'beads') {
        ctx.fillStyle = bead.color.hex;
        ctx.beginPath(); ctx.arc(cx, cy, scale/2 - 2, 0, Math.PI*2); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.1)'; ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.beginPath(); ctx.arc(cx - scale/6, cy - scale/6, scale/8, 0, Math.PI*2); ctx.fill();
      } else {
        ctx.fillStyle = bead.color.hex;
        ctx.fillRect(bead.x * scale + 1, bead.y * scale + 1, scale - 2, scale - 2);
      }

      if (showSymbols) {
        const bright = (bead.color.r * 299 + bead.color.g * 587 + bead.color.b * 114) / 1000;
        ctx.fillStyle = bright > 128 ? 'rgba(0,0,0,0.7)' : 'white';
        ctx.font = `bold ${scale/2.5}px monospace`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(bead.color.id, cx, cy);
      }
    });

    ctx.strokeStyle = 'rgba(0,0,0,0.05)';
    ctx.lineWidth = 1;
    for(let x=0; x<=w; x++) { ctx.beginPath(); ctx.moveTo(x*scale, 0); ctx.lineTo(x*scale, h*scale); ctx.stroke(); }
    for(let y=0; y<=h; y++) { ctx.beginPath(); ctx.moveTo(0, y*scale); ctx.lineTo(w*scale, y*scale); ctx.stroke(); }

    if (showPlaques) {
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 3;
      for(let x=0; x<=w; x+=29) { ctx.beginPath(); ctx.moveTo(x*scale, 0); ctx.lineTo(x*scale, h*scale); ctx.stroke(); }
      for(let y=0; y<=h; y+=29) { ctx.beginPath(); ctx.moveTo(0, y*scale); ctx.lineTo(w*scale, y*scale); ctx.stroke(); }
    }
  };

  const handleDownload = () => {
    if (isExporting || !displayCanvasRef.current) return;
    setIsExporting(true);

    try {
      displayCanvasRef.current.toBlob((blob) => {
        if (!blob) {
          throw new Error("Échec de la génération du fichier.");
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.download = `hama-pro-studio-${settings.width}x${settings.height}.png`;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        
        // Nettoyage immédiat
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setIsExporting(false);
        }, 100);
      }, 'image/png', 1.0);
    } catch (error) {
      console.error("Erreur d'export:", error);
      alert("Une erreur est survenue lors de l'exportation HD.");
      setIsExporting(false);
    }
  };

  useEffect(() => { processImage(); }, [originalImage, settings, showSymbols, showPlaques, viewMode]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200">
        <div className="bg-slate-900 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setViewMode(v => v === 'beads' ? 'blueprint' : 'beads')}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${viewMode === 'beads' ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-300'}`}
            >
              <SafeIcon icon={viewMode === 'beads' ? FiEye : FiLayers} />
              <span>{viewMode === 'beads' ? 'Rendu Réel' : 'Vue Plan'}</span>
            </button>
            <button 
              onClick={() => setShowSymbols(!showSymbols)}
              className={`p-1.5 rounded-lg transition-all ${showSymbols ? 'text-purple-400' : 'text-slate-500 hover:text-white'}`}
              title="Afficher les IDs"
            >
              <SafeIcon icon={FiType} className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setShowPlaques(!showPlaques)}
              className={`p-1.5 rounded-lg transition-all ${showPlaques ? 'text-blue-400' : 'text-slate-500 hover:text-white'}`}
              title="Afficher les plaques"
            >
              <SafeIcon icon={FiGrid} className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex bg-slate-800 rounded-lg p-1">
              <button onClick={() => setZoom(z => Math.max(0.1, z-0.1))} className="p-1 text-slate-400 hover:text-white"><SafeIcon icon={FiZoomOut} /></button>
              <span className="px-2 text-[10px] font-mono text-slate-300 flex items-center">{Math.round(zoom*100)}%</span>
              <button onClick={() => setZoom(z => Math.min(3, z+0.1))} className="p-1 text-slate-400 hover:text-white"><SafeIcon icon={FiZoomIn} /></button>
            </div>
            <button 
              onClick={handleDownload} 
              disabled={isExporting || !originalImage}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center space-x-2 transition-all active:scale-95 ${isExporting || !originalImage ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg'}`}
            >
              <SafeIcon icon={isExporting ? FiLoader : FiDownload} className={isExporting ? 'animate-spin' : ''} />
              <span>{isExporting ? 'GÉNÉRATION...' : 'EXPORT HD'}</span>
            </button>
          </div>
        </div>

        <div className="bg-slate-200 h-[700px] overflow-auto relative flex items-center justify-center p-10 cursor-grab active:cursor-grabbing">
          {isProcessing && (
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-md z-30 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white font-black tracking-widest uppercase text-sm">Optimisation du rendu...</p>
              </div>
            </div>
          )}
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.15s ease-out' }}>
            <canvas ref={displayCanvasRef} className="shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-sm bg-white" />
          </div>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-xl font-black text-slate-900 flex items-center">
            <SafeIcon icon={FiList} className="mr-3 text-purple-600" />
            Inventaire des Perles
          </h3>
          <div className="text-sm font-bold text-slate-400">
            Total : {Object.values(beadCount).reduce((a, b) => a + b, 0)} perles
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.entries(beadCount).sort(([,a],[,b]) => b - a).map(([id, count]) => {
            const color = hamaColors.find(c => c.id === id);
            return (
              <motion.div key={id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="group p-4 bg-slate-50 rounded-2xl border border-transparent hover:border-purple-200 hover:bg-white transition-all">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-1 bg-white rounded-md text-[10px] font-black border border-slate-200 shadow-sm">#{id}</span>
                  <span className="text-sm font-black text-purple-600">x{count}</span>
                </div>
                <div className="w-full h-8 rounded-lg shadow-inner mb-2 group-hover:scale-105 transition-transform" style={{ backgroundColor: color?.hex }} />
                <p className="text-[10px] font-bold text-slate-500 uppercase truncate">{color?.name}</p>
                <p className="text-[9px] text-slate-400 mt-1 italic">{Math.ceil(count / 1000)} sachet(s)</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PixelArtCanvas;